// The live hunt scene. The map is painted once per area from the same generator
// the server walks on (shared/worldmap.ts); every frame we draw a camera window
// of it plus the creatures, the trainer, tall props and effects, sorted by depth.
// Server snapshots arrive every tick and positions are interpolated between them.
import { CONFIG } from '../shared/config.ts';
import { tutorialHolds } from '../shared/tutorial.ts';
import { SPECIES } from '../shared/species.ts';
import { displayName } from '../shared/nametags.ts';
import { MOVE, WAVE_RADIUS, type AnimKind } from '../shared/moves.ts';
import { ELEMENTS, type Element } from '../shared/elements.ts';
import { effectWord } from './moveclock.ts';
import { GLYPH } from './moveclock.ts';
import { mapFor, type WorldMap } from '../shared/worldmap.ts';
import { paintTerrain, paintPaths } from './terrain.ts';
import { waymarksFor, signText, type Exit } from './waymarks.ts';
import { AREA } from '../shared/grounds.ts';
import type { GameEvent, WildDTO } from '../shared/protocol.ts';
import { canvasDpr, frameTime, onLite } from './perf.ts';
import { sprite, spriteSize, has, sheet, drawable as ready, silhouette, sheetInfo, sheetCell, artInfo, type SheetInfo } from './assets.ts';
import { myArt, artOf } from './skins.ts';
import { heading, steer, holdDir, holdFace, type Dir, type Heading } from './facing.ts';
import { EL_RAMP, particleSprite, sparkSprite, stamp } from './hitfx.ts';
import { S, serverNow } from './state.ts';
import { opensBetween } from './hud.ts';
import { mySprite, trainerSprite } from './trainerart.ts';
import { startTravel, drawTravel, travelCovers, skipTravel, type Walker } from './travel.ts';
import { stepWayfarers, myShowcase, myEmote, SHOW_MS, type Wayfarer } from './wayfarers.ts';
import { emoteIcon } from './kinhall.ts';
import { EMOTE_MS } from '../shared/kinhall.ts';
import { throwStepShowing } from './tutorial.ts';

/** Source pixels per map tile. */
const TILE = 24;

type Fx =
  | { k: 'num'; x: number; y: number; text: string; color: string; t0: number; big?: boolean; huge?: boolean }
  /** A move landing (watch mode): from the lead at (fx, fy) to the target at (x, y). */
  | { k: 'move'; kind: AnimKind; el: Element; x: number; y: number; fx: number; fy: number; t0: number; seed: number; /** An Awakened move: the Signature drawn grander, in gilt. */ awk?: boolean }
  | { k: 'throw'; uid: number; fx: number; fy: number; t0: number }
  | { k: 'burst'; x: number; y: number; color: string; t0: number; n: number }
  | { k: 'banner'; text: string; color: string; t0: number };

const fx: Fx[] = [];
let canvas: HTMLCanvasElement;
let g: CanvasRenderingContext2D;
let dpr = 1;
let zoom = Number(localStorage.getItem('mi-zoom')) || 2;
/** Screen pixels per source pixel. */
let K = 2;
let cam = { x: 0, y: 0, ok: false };
/** Where the camera was over the last moments. A tap lands where the player saw the ring, and the view keeps panning
 *  as the lead walks to its next fight: on a phone a beaten wild slid 50 to 110 px in half a second, out from under
 *  the finger (2026-10-25). */
const camTrail: { t: number; x: number; y: number }[] = [];
const TRAIL_MS = 500;
let terrain: { key: string; c: HTMLCanvasElement; decorDone: boolean } | null = null;

// The trainer walks behind the lead creature along the lead's own trail.
const trail: { x: number; y: number }[] = [];
let trainer = { x: 0, y: 0, dir: 'down' as Dir, walk: 0, moving: false, throwAt: -1e9, head: heading() };
/** Which unlock a level-up banner names when one level opens several: a new region first, then the big systems. */
const HEADLINE = ['region:', 'arena', 'trail', 'halls', 'guild'];
const headline = (id: string) => { const i = HEADLINE.findIndex((h) => id.startsWith(h)); return i < 0 ? HEADLINE.length : i; };
const dirTo = (dx: number, dy: number): Dir => (Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up');

export function mountField(c: HTMLCanvasElement) {
  canvas = c;
  g = canvas.getContext('2d')!;
  const fit = () => {
    dpr = canvasDpr();
    const r = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(r.width * dpr));
    canvas.height = Math.max(1, Math.round(r.height * dpr));
  };
  new ResizeObserver(fit).observe(canvas);
  onLite(fit);
  if (typeof IntersectionObserver === 'function') new IntersectionObserver((es) => { shown = es[es.length - 1].isIntersecting; }).observe(canvas);
  fit();
  // A click during the walk between hunts (client/travel.ts) skips it and goes no further. A click on a signpost
  // (and not on a body to catch) opens the area its path leads to on the map.
  canvas.addEventListener('click', (e) => {
    if (skipTravel()) { e.stopImmediatePropagation(); return; }
    // The tutorial's throw step keeps every tap on the scene for the seal (client/catch.ts).
    const to = throwStepShowing() ? null : signpostAt(e.clientX, e.clientY);
    if (to && AREA[to] && catchableAt(e.clientX, e.clientY, () => false) === null) { e.stopImmediatePropagation(); location.hash = `#/map/${AREA[to].region}/${to}`; }
  }, true);
  if (!saver) { looping = true; requestAnimationFrame(frame); }
}

// Battery saver: the scene isn't drawn at all. The animation loop stops (no
// frames are even requested) and effects are dropped; the server keeps
// hunting and the panels keep updating from its messages.
let saver = (() => { try { return localStorage.getItem('mi-saver') === '1'; } catch { return false; } })();
let looping = false;
export const batterySaver = () => saver;
export function setBatterySaver(on: boolean) {
  saver = on;
  try { localStorage.setItem('mi-saver', on ? '1' : '0'); } catch { /* storage off */ }
  fx.length = 0;
  if (!on && canvas && !looping) { looping = true; lastT = performance.now(); requestAnimationFrame(frame); }
}

// Reduced motion (the system setting): no move effects, and numbers fade where they appear instead of rising.
const motionQuery = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
let reduce = !!motionQuery?.matches;
motionQuery?.addEventListener?.('change', (e) => { reduce = e.matches; if (reduce) for (let i = fx.length - 1; i >= 0; i--) if (fx[i].k === 'move') fx.splice(i, 1); });
export const reducedMotion = () => reduce;

export function setZoom(z: number) {
  zoom = Math.max(1, Math.min(4, z));
  try { localStorage.setItem('mi-zoom', String(zoom)); } catch { /* private mode */ }
}
export function getZoom() { return zoom; }

// ---------------- manual catch ----------------
// A defeated wild that has not had its throw can be clicked to throw a seal at it (client/catch.ts).
let catchHover: number | null = null;
export function setCatchHover(uid: number | null) { catchHover = uid; }

/** The catchable body nearest a page point (client px), within a finger's reach (or at any distance when `anywhere`); null when none. */
export function catchableAt(clientX: number, clientY: number, skip: (uid: number) => boolean, anywhere = false): number | null {
  const f = S.field;
  if (!f || !canvas || saver || !onScreen() || travelCovers()) return null;
  const r = canvas.getBoundingClientRect();
  const px = (clientX - r.left) * dpr, py = (clientY - r.top) * dpr;
  const alpha = Math.min(1, (performance.now() - S.fieldAt) / CONFIG.tickMs);
  const now = serverNow();
  const t = performance.now();
  const views = camTrail.length ? camTrail : [{ t, x: cam.x, y: cam.y }];
  let best: number | null = null, bd = Infinity;
  for (const w of f.wilds) {
    if (!w.downUntil || w.tried || w.throwing || w.downUntil <= now || skip(w.uid)) continue;
    const p = wildPos(w, alpha);
    // Nearest to where it was drawn at any moment of the last TRAIL_MS, the present a little preferred.
    let d = Infinity;
    for (const c of views) {
      const at = Math.hypot(px - (p.x * TILE - c.x) * K, py - ((p.y * TILE - c.y) * K - 9 * K));
      d = Math.min(d, at + ((t - c.t) / TRAIL_MS) * 8 * dpr);
    }
    if ((anywhere || d < Math.max(14 * K, 24 * dpr)) && d < bd) { best = w.uid; bd = d; }
  }
  return best;
}

function drawCatchMark(cx: number, cy: number, hover: boolean) {
  const img = sprite('icon-rune-seal');
  if (!ready(img)) return;
  const s = (hover ? 9 : 7) * K;
  g.globalAlpha = hover ? 1 : 0.85;
  g.drawImage(img, Math.round(cx - s / 2), Math.round(cy - s / 2), Math.round(s), Math.round(s));
  g.globalAlpha = 1;
}

// ---------------- terrain painting (client/terrain.ts) ----------------
const rgb = (h: string): [number, number, number] => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
export { paintTerrain };

/**
 * Draw a prop 1:1 in map pixels with its base at (px, ground). Rig-drawn art (art/rig3d/props)
 * stands on its own ground line; old sprites stand on their bottom edge.
 */
export function stampProp(x: CanvasRenderingContext2D, id: string, px: number, ground: number, frame = 0) {
  const img = sprite(id), a = artInfo(id);
  if (a) { x.drawImage(img, (frame % a.frames) * a.w, 0, a.w, a.h, Math.round(px - a.w / 2), Math.round(ground - (a.h - a.foot)), a.w, a.h); return; }
  const sz = spriteSize(id);
  x.drawImage(img, Math.round(px - sz / 2), Math.round(ground - sz));
}

/** Ground decor (grass tufts, flowers) goes straight into the terrain canvas. Returns false while sprites load. */
export function paintDecor(m: WorldMap, c: HTMLCanvasElement): boolean {
  const x = c.getContext('2d')!;
  let ready = true;
  for (const p of m.props) {
    if (p.block || !has(p.id)) continue;
    const img = sprite(p.id) as HTMLImageElement;
    if (!img.complete) { ready = false; continue; }
    stampProp(x, p.id, p.x * TILE, p.y * TILE + 4);
  }
  return ready;
}

function terrainFor(groundId: string) {
  const m = mapFor(groundId);
  if (!terrain || terrain.key !== m.key) {
    // Moving to another area walks there on the region map first.
    if (terrain) startTravel(terrain.key, m.key, reduce);
    const c = paintTerrain(m);
    // The worn paths out to the neighbouring hunts (client/waymarks.ts).
    paintPaths(m, c, waymarksFor(m).exits.map((e) => e.path));
    terrain = { key: m.key, c, decorDone: false };
  }
  if (!terrain.decorDone) terrain.decorDone = paintDecor(m, terrain.c);
  return { m, c: terrain.c };
}

// ---------------- helpers ----------------
function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }

function wildPos(w: WildDTO, alpha: number) {
  const p = S.prevField?.wilds.find((x) => x.uid === w.uid);
  return p ? { x: lerp(p.x, w.x, alpha), y: lerp(p.y, w.y, alpha) } : { x: w.x, y: w.y };
}

function leadPos(alpha: number) {
  const f = S.field!;
  if (!f.lead) return null;
  const p = S.prevField?.lead;
  return p && p.id === f.lead.id ? { x: lerp(p.x, f.lead.x, alpha), y: lerp(p.y, f.lead.y, alpha) } : { x: f.lead.x, y: f.lead.y };
}

/** World (tile units) to screen pixels. */
const sx = (x: number) => Math.round((x * TILE - cam.x) * K);
const sy = (y: number) => Math.round((y * TILE - cam.y) * K);

function drawImg(img: CanvasImageSource, x: number, foot: number, w: number, h: number, flip = false, alpha = 1, squash = 1) {
  g.globalAlpha = alpha;
  g.save();
  g.translate(sx(x), sy(foot));
  if (flip) g.scale(-1, 1);
  g.drawImage(img, Math.round((-w / 2) * K), Math.round(-h * squash * K), Math.round(w * K), Math.round(h * squash * K));
  g.restore();
  g.globalAlpha = 1;
}

function shadow(x: number, y: number, w: number, alpha = 0.22) {
  g.globalAlpha = alpha;
  g.fillStyle = '#000';
  g.beginPath(); g.ellipse(sx(x), sy(y), w * 0.32 * K, w * 0.09 * K, 0, 0, Math.PI * 2); g.fill();
  g.globalAlpha = 1;
}


// ---------------- animated creatures ----------------
// Creatures drawn with the 3D rig have a sheet: rows are facings, columns are
// walk, idle and attack frames. Each creature on screen remembers which way it
// faces and when it last moved, attacked or got hit.
type Facing = 'front' | 'left' | 'back' | 'right';
const ROW: Record<Facing, number> = { front: 0, left: 1, back: 2, right: 3 };
/** Logical pixels per sheet pixel (sheet cells are 80px, bigger than the old 32-64px sprites). */
const SHEET_SCALE = 0.75;
const ATK_MS = 70, WALK_MS = 140, IDLE_MS = 150, FLASH_MS = 110;
interface Anim { face: Facing; x: number; y: number; moved: number; atk: number; hit: number; seed: number; seen: number; head: Heading }
const anims = new Map<string, Anim>();
/** Every wild gets an entry by uid, so entries not drawn for 30 s are dropped (checked every 10 s); a tab left open would otherwise keep every wild it ever showed. */
const ANIM_KEEP_MS = 30_000, ANIM_PRUNE_MS = 10_000;
let animPruned = 0;

function animFor(key: string, x: number, y: number, now: number): Anim {
  if (now - animPruned > ANIM_PRUNE_MS) {
    animPruned = now;
    for (const [k, v] of anims) if (now - v.seen > ANIM_KEEP_MS) anims.delete(k);
  }
  let a = anims.get(key);
  if (!a) { a = { face: 'front', x, y, moved: 0, atk: -1e9, hit: -1e9, seed: Math.random() * 5000, seen: now, head: heading() }; anims.set(key, a); }
  const dt = now - a.seen;
  a.seen = now;
  const dx = x - a.x, dy = y - a.y;
  if (Math.hypot(dx, dy) > 0.004) {
    a.moved = now;
    steer(a.head, dx, dy, dt);
    a.face = holdFace(a.face, a.head.vx, a.head.vy);
  }
  a.x = x; a.y = y;
  return a;
}

// Top of each creature's front idle frame (sheet px), so labels sit just above it.
const tops = new Map<string, number>();
function sheetTop(id: string, img: CanvasImageSource, info: SheetInfo): number {
  let top = tops.get(id);
  if (top !== undefined) return top;
  const n = sheetCell(info);
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const x = c.getContext('2d', { willReadFrequently: true })!;
  x.drawImage(img, info.walk * n, 0, n, n, 0, 0, n, n);
  const d = x.getImageData(0, 0, n, n).data;
  top = 0;
  find: for (let y = 0; y < n; y++) for (let i = 0; i < n; i++) if (d[(y * n + i) * 4 + 3] === 255) { top = y; break find; }
  top = top * info.size / n;
  tops.set(id, top);
  return top;
}

function drawSheet(id: string, aura: boolean, x: number, y: number, a: Anim, t: number, opts: { alpha?: number; squash?: number; face?: Facing }) {
  const info = sheetInfo(id)!;
  const img = sheet(id, aura);
  if (!ready(img)) return;
  const n = info.size, p = sheetCell(info), now = performance.now();
  const face = opts.face ?? a.face;
  let col: number;
  if (now - a.atk < info.atk * ATK_MS) col = info.walk + info.idle + Math.floor((now - a.atk) / ATK_MS);
  else if (now - a.moved < 160) col = Math.floor(t / WALK_MS) % info.walk;
  else col = info.walk + (Math.floor((t + a.seed) / IDLE_MS) % info.idle);
  const flash = now - a.hit > 0 && now - a.hit < FLASH_MS;
  const sx0 = col * p, sy0 = ROW[face] * p;
  const w = n * SHEET_SCALE, squash = opts.squash ?? 1;
  g.globalAlpha = opts.alpha ?? 1;
  // The sheet's ground point is 7px above the cell bottom.
  const foot = sy(y + 0.12) + 7 * SHEET_SCALE * K;
  const dx = Math.round(sx(x) - (w / 2) * K), dy = Math.round(foot - w * squash * K), dw = Math.round(w * K), dh = Math.round(w * squash * K);
  g.drawImage(img, sx0, sy0, p, p, dx, dy, dw, dh);
  // The hit flash: the frame in white over itself.
  if (flash) { g.globalAlpha = (opts.alpha ?? 1) * 0.85; g.drawImage(silhouette(img, sx0, sy0, p, '#fff'), 0, 0, p, p, dx, dy, dw, dh); }
  g.globalAlpha = 1;
}
/** Height above the feet (logical px) where a creature's head ends, for labels. */
function creatureHeight(id: string): number {
  const info = sheetInfo(id);
  if (!info) return spriteSize(id) * 0.85;
  const img = sheet(id);
  if (!ready(img)) return info.size * SHEET_SCALE * 0.7;
  return (info.size - 7 - sheetTop(id, img, info)) * SHEET_SCALE;
}

function drawCreature(id: string, aura: boolean, x: number, y: number, faceRight: boolean, opts: { alpha?: number; squash?: number; bob?: number; key?: string; face?: Facing; t?: number } = {}) {
  if (sheetInfo(id) && opts.key) {
    const a = animFor(opts.key, x, y, performance.now());
    drawSheet(id, aura, x, y, a, opts.t ?? performance.now(), opts);
    return;
  }
  const img = sprite(id, aura);
  if (!ready(img)) return;
  const size = spriteSize(id);
  shadow(x, y, size, 0.25 * (opts.alpha ?? 1));
  drawImg(img, x, y + (opts.bob ?? 0) / TILE + 0.12, size, size, faceRight, opts.alpha ?? 1, opts.squash ?? 1);
}

function label(s: string, x: number, y: number, color: string, px = 11) {
  g.font = `600 ${Math.round(px * dpr)}px Inter, sans-serif`;
  g.textAlign = 'center';
  g.lineJoin = 'round';
  g.lineWidth = 3 * dpr; g.strokeStyle = 'rgba(16,19,31,.85)'; g.strokeText(s, x, y);
  g.fillStyle = color; g.fillText(s, x, y);
}

const UNITS = ['', 'k', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx'];
/** A floating damage number: exact up to 9,999, then three figures and a unit (12.3k, 456M, 22.1T), so overkill never runs off the scene. */
export function dmgText(n: number): string {
  if (n < 1e4) return (Math.round(n) || 0).toLocaleString('en-US');
  let u = 0;
  while (n >= 999.5 && u < UNITS.length - 1) { n /= 1000; u++; }
  return `${n >= 99.95 ? n.toFixed(0) : n >= 9.995 ? n.toFixed(1) : n.toFixed(2)}${UNITS[u]}`;
}

/** A wild's name tag, placed after the others so bunched-up tags don't print over each other. */
interface Tag { s: string; x: number; y: number; color: string; pri: number }
/**
 * Draw name tags without overlaps: the most important first (aura, then the one being fought, then
 * the nearest), each nudged up a line or two when its spot is taken, and left out when it can't
 * fit. `taken` starts with boxes that are already drawn (the lead's own tag).
 */
function placeTags(tags: Tag[], taken: { l: number; r: number; t: number; b: number }[]) {
  g.font = `600 ${Math.round(11 * dpr)}px Inter, sans-serif`;
  const h = 13 * dpr, pad = 2 * dpr;
  tags.sort((a, b) => b.pri - a.pri);
  for (const tag of tags) {
    const w = g.measureText(tag.s).width;
    for (const dy of [0, -h, -2 * h]) {
      const box = { l: tag.x - w / 2 - pad, r: tag.x + w / 2 + pad, t: tag.y + dy - h + 2 * dpr, b: tag.y + dy + 2 * dpr };
      if (taken.some((o) => box.l < o.r && box.r > o.l && box.t < o.b && box.b > o.t)) continue;
      taken.push(box);
      label(tag.s, tag.x, tag.y + dy, tag.color);
      break;
    }
  }
}

function bigText(s: string, x: number, y: number, color: string, px = 22) {
  g.font = `700 ${Math.round(px * dpr)}px Inter, sans-serif`;
  g.textAlign = 'center';
  g.lineJoin = 'round';
  g.lineWidth = 5 * dpr; g.strokeStyle = '#10131f'; g.strokeText(s, x, y);
  g.fillStyle = color; g.fillText(s, x, y);
}

function hpBar(x: number, y: number, frac: number, wide = 26) {
  const w = Math.round(wide * dpr), h = Math.max(3, Math.round(3 * dpr));
  const px = Math.round(x - w / 2), py = Math.round(y);
  g.fillStyle = '#10131f'; g.fillRect(px - dpr, py - dpr, w + 2 * dpr, h + 2 * dpr);
  g.fillStyle = frac > 0.5 ? '#79b04a' : frac > 0.2 ? '#f0c45a' : '#e05a5a';
  g.fillRect(px, py, Math.round(w * Math.max(0, frac)), h);
}

// A trainer outfit drawn with the 3D rig has a sheet like a creature's (art/rig3d/build-trainer.ts):
// it walks while following, breathes and blinks while waiting, and winds up and throws each seal.
const FACING: Record<Dir, Facing> = { down: 'front', up: 'back', left: 'left', right: 'right' };
const trainerAnim: Anim = { face: 'front', x: 0, y: 0, moved: 0, atk: -1e9, hit: -1e9, seed: 0, seen: 0, head: heading() };

function drawTrainer(t: number) {
  const fit = mySprite();
  if (sheetInfo(fit)) {
    const now = performance.now();
    trainerAnim.face = FACING[trainer.dir];
    if (trainer.moving) trainerAnim.moved = now;
    // Start on the wind-up's peak so the seal leaves the hand as its arc begins.
    trainerAnim.atk = trainer.throwAt - ATK_MS;
    drawSheet(fit, false, trainer.x, trainer.y + 0.03, trainerAnim, now, {});
    return;
  }
  const id = `${fit}-${trainer.dir}-${trainer.moving ? 1 + (Math.floor(trainer.walk) % 2) : 0}`;
  if (has(id)) {
    const img = sprite(id);
    if (!ready(img)) return;
    shadow(trainer.x, trainer.y + 0.1, 20);
    drawImg(img, trainer.x, trainer.y + 0.15, 32, 32);
    return;
  }
  // Until the trainer sprite exists: a simple cloaked figure.
  const x = sx(trainer.x), y = sy(trainer.y + 0.15);
  shadow(trainer.x, trainer.y + 0.1, 20);
  const bob = trainer.moving ? Math.round(Math.sin(t / 90)) * K : 0;
  g.fillStyle = '#1b2238'; g.fillRect(x - 6 * K, y - 20 * K + bob, 12 * K, 20 * K);
  g.fillStyle = '#8a3fc4'; g.fillRect(x - 5 * K, y - 15 * K + bob, 10 * K, 14 * K);
  g.fillStyle = '#e3a790'; g.fillRect(x - 3 * K, y - 19 * K + bob, 6 * K, 5 * K);
}

function updateTrainer(lp: { x: number; y: number } | null, dtMs: number) {
  if (!lp) return;
  const last = trail[trail.length - 1];
  if (!last || Math.hypot(last.x - lp.x, last.y - lp.y) > 0.08) trail.push({ x: lp.x, y: lp.y });
  if (trail.length > 400) trail.splice(0, trail.length - 400);
  // Find the point on the trail ~1.2 tiles behind the lead.
  let want = 1.2, target = trail[0] ?? lp;
  for (let i = trail.length - 1; i > 0; i--) {
    const seg = Math.hypot(trail[i].x - trail[i - 1].x, trail[i].y - trail[i - 1].y);
    if (seg >= want) { const k = want / seg; target = { x: lerp(trail[i].x, trail[i - 1].x, k), y: lerp(trail[i].y, trail[i - 1].y, k) }; break; }
    want -= seg;
    target = trail[i - 1];
  }
  if (!trainer.x && !trainer.y) { trainer.x = target.x; trainer.y = target.y + 0.6; }
  const dx = target.x - trainer.x, dy = target.y - trainer.y, d = Math.hypot(dx, dy);
  const speed = (CONFIG.walkTilesPerSec * 1.15 * dtMs) / 1000;
  if (d > 0.05) {
    const step = Math.min(d, speed);
    trainer.x += (dx / d) * step; trainer.y += (dy / d) * step;
    trainer.moving = d > 0.12;
    trainer.walk += dtMs / 180;
    trainer.dir = holdDir(trainer.dir, steer(trainer.head, dx, dy, dtMs).vx, trainer.head.vy);
  } else {
    trainer.moving = false;
    // Face the action when standing (a throw keeps facing its target until it lands).
    const ldx = lp.x - trainer.x, ldy = lp.y - trainer.y;
    if (Math.hypot(ldx, ldy) > 0.3 && performance.now() - trainer.throwAt > 600) trainer.dir = holdDir(trainer.dir, ldx, ldy);
  }
}

/** Canvas pixels at the top of the field hidden by a hunt card that spans it (phones), checked twice a second. */
let cover = { at: -1e9, px: 0, card: 0 };
function coverTop(t: number) {
  if (t - cover.at < 500) return cover.px;
  const card = document.getElementById('fieldhead');
  const cr = canvas.getBoundingClientRect();
  const r = card?.getBoundingClientRect();
  const under = r && r.top < cr.top + 40 ? Math.max(0, r.bottom - cr.top) * dpr : 0;
  cover = { at: t, px: r && r.width > cr.width * 0.8 ? under : 0, card: under };
  return cover.px;
}
/** Canvas pixels down to the bottom of the hunt card, however wide it is: the walk between hunts puts its names below it. */
const cardBottom = (t: number) => (coverTop(t), cover.card);

// ---------------- frame ----------------
let lastT = performance.now();
let lastLeadX = 0;
/** False while a page covers the hunt (the field's container is display: none). Reads layout: for clicks, not every frame. */
const onScreen = () => !!canvas && canvas.offsetParent !== null;
/**
 * The same, kept by an IntersectionObserver (mountField) for the frame loop and incoming events. Reading offsetParent
 * every frame forced a layout whenever a page (the Trail board, a battle) had changed the page since the last frame.
 */
let shown = true;
const shownNow = () => !!canvas && shown;

function frame(t: number) {
  if (saver) { looping = false; fx.length = 0; return; }
  requestAnimationFrame(frame);
  if (!shownNow()) { fx.length = 0; lastT = t; return; }
  const dtMs = Math.min(100, t - lastT);
  lastT = t;
  frameTime(dtMs);
  const f = S.field;
  const W = canvas.width, H = canvas.height;
  g.imageSmoothingEnabled = false;
  g.fillStyle = '#0d1018';
  g.fillRect(0, 0, W, H);
  if (!f) return;
  const { m, c: bg } = terrainFor(f.ground);
  K = zoom * dpr;
  // The walk between hunts covers the scene: draw only that.
  const lc = f.lead ? S.creatures.get(f.lead.id) : undefined;
  const walkers: [Walker | null, Walker | null] = [{ id: mySprite(), aura: false }, lc ? { id: myArt(lc.species, lc.rolls.aura), aura: lc.rolls.aura } : null];
  if (travelCovers(t)) { drawTravel(g, W, H, dpr, t, cardBottom(t), ...walkers); return; }
  const alpha = Math.min(1, (performance.now() - S.fieldAt) / CONFIG.tickMs);
  const now = serverNow();
  const lp = leadPos(alpha);

  // Camera: follow the lead, never show past the map edge.
  const viewW = W / K, viewH = H / K;
  const focus = lp ?? m.start;
  // On a phone the hunt card spans the top of the field: centre the fight in the part left uncovered.
  const wantX = focus.x * TILE - viewW / 2, wantY = focus.y * TILE - viewH / 2 - coverTop(t) / 2 / K;
  const clampX = (v: number) => (viewW >= m.w * TILE ? (m.w * TILE - viewW) / 2 : Math.max(0, Math.min(m.w * TILE - viewW, v)));
  const clampY = (v: number) => (viewH >= m.h * TILE ? (m.h * TILE - viewH) / 2 : Math.max(0, Math.min(m.h * TILE - viewH, v)));
  if (!cam.ok) { cam = { x: clampX(wantX), y: clampY(wantY), ok: true }; }
  const ease = 1 - Math.pow(0.004, dtMs / 1000);
  cam.x = clampX(lerp(cam.x, wantX, ease));
  cam.y = clampY(lerp(cam.y, wantY, ease));
  cam.x = Math.round(cam.x * K) / K; cam.y = Math.round(cam.y * K) / K;
  camTrail.push({ t: performance.now(), x: cam.x, y: cam.y });
  while (camTrail[0].t < camTrail[camTrail.length - 1].t - TRAIL_MS) camTrail.shift();

  // Ground
  g.drawImage(bg, cam.x, cam.y, viewW, viewH, 0, 0, W, H);

  updateTrainer(lp, dtMs);

  const inView = (x: number, y: number, pad = 3) => x * TILE > cam.x - pad * TILE && x * TILE < cam.x + viewW + pad * TILE && y * TILE > cam.y - pad * TILE && y * TILE < cam.y + viewH + pad * TILE;
  const items: { y: number; draw: () => void }[] = [];
  const overlays: (() => void)[] = [];
  const tags: Tag[] = [];
  let leadTag: { l: number; r: number; t: number; b: number } | null = null;

  // The area's landmark and the signposts where paths lead out to the next hunts (client/waymarks.ts). Trees and rocks
  // in a path's gap or under the landmark aren't drawn; those tiles stay walls all the same.
  const wm = waymarksFor(m), lm = wm.landmark;
  const lmImg = lm && has(lm.id) ? sprite(lm.id) : null, lmArt = lm && artInfo(lm.id);
  const lmShown = !!(lm && lmImg && lmArt && ready(lmImg));
  if (lm && lmShown && inView(lm.x, lm.y, 7)) {
    const a = lmArt!, fr = a.frames > 1 ? Math.floor(t / 240) % a.frames : 0;
    items.push({ y: lm.y, draw: () => g.drawImage(lmImg!, fr * a.w, 0, a.w, a.h, Math.round(sx(lm.x) - (a.w / 2) * K), Math.round(sy(lm.y) - (a.h - a.foot) * K), Math.round(a.w * K), Math.round(a.h * K)) });
  }
  drawnSigns = [];
  const signId = `prop-sign-${AREA[m.key]?.region ?? 'midgard'}`, signImg = has(signId) ? sprite(signId) : null, signArt = artInfo(signId);
  for (const e of wm.exits) {
    if (!signImg || !signArt || !ready(signImg) || !inView(e.sign.x, e.sign.y)) continue;
    const a = signArt, to = AREA[e.to];
    // The plank points along the path out: off the map's side edges, or across to the path at the top and bottom.
    const flip = e.edge === 'left' || ((e.edge === 'top' || e.edge === 'bottom') && e.sign.x > e.path[0].x);
    items.push({ y: e.sign.y, draw: () => {
      const x = sx(e.sign.x), y = sy(e.sign.y) - (a.h - a.foot) * K;
      g.save();
      g.translate(x, 0);
      if (flip) g.scale(-1, 1);
      g.drawImage(signImg, 0, 0, a.w, a.h, Math.round((-a.w / 2) * K), Math.round(y), Math.round(a.w * K), Math.round(a.h * K));
      g.restore();
    } });
    const open = !to || !S.trainer || S.trainer.level >= to.level;
    const arrow = { left: '← ', right: '', top: '↑ ', bottom: '↓ ' }[e.edge], tail = e.edge === 'right' ? ' →' : '';
    const text = `${arrow}${signText(e)}${tail}${open ? '' : ` · Lv ${to!.level}`}`;
    const top = sy(e.sign.y) - (a.h - a.foot) * K - 3 * dpr;
    overlays.push(() => {
      // Kept inside the scene: signs stand at the map's edges.
      g.font = `600 ${Math.round(11 * dpr)}px Inter, sans-serif`;
      const half = g.measureText(text).width / 2 + 4 * dpr;
      tags.push({ s: text, x: Math.max(half, Math.min(W - half, sx(e.sign.x))), y: top, color: open ? '#ead9ae' : '#9aa6c0', pri: -10 });
    });
    drawnSigns.push({ to: e.to, x: sx(e.sign.x), top, bottom: sy(e.sign.y) });
  }

  // Tall props (trees, rocks, columns) take part in depth sorting.
  for (const p of m.props) {
    if (!p.block || !inView(p.x, p.y)) continue;
    if (!has(p.id)) continue;
    const ti = Math.floor(p.y) * m.w + Math.floor(p.x);
    if (wm.hidden.has(ti) || (lmShown && lm!.hide.has(ti))) continue;
    const img = sprite(p.id);
    if (!ready(img)) continue;
    const a = artInfo(p.id);
    if (a) {
      // Rig-drawn props carry their own soft shadow; trees sway out of step with each other.
      const f = a.frames > 1 ? Math.floor(t / 320 + p.x * 3.7 + p.y * 1.3) % a.frames : 0;
      items.push({ y: p.y + 0.3, draw: () => g.drawImage(img, f * a.w, 0, a.w, a.h, Math.round(sx(p.x) - (a.w / 2) * K), Math.round(sy(p.y + 0.42) - (a.h - a.foot) * K), Math.round(a.w * K), Math.round(a.h * K)) });
      continue;
    }
    const sz = spriteSize(p.id);
    items.push({ y: p.y + 0.3, draw: () => { shadow(p.x, p.y + 0.35, sz * 0.9, 0.18); drawImg(img, p.x, p.y + 0.42, sz, sz); } });
  }

  for (const w of f.wilds) {
    const p = wildPos(w, alpha);
    if (!inView(p.x, p.y)) continue;
    const name = SPECIES[w.species]?.name ?? w.species;
    items.push({ y: p.y, draw: () => {
      if (w.downUntil) {
        drawCreature(w.species, w.aura, p.x, p.y, false, { alpha: 0.7, squash: 0.55, key: `w${w.uid}`, t });
        overlays.push(() => {
          const leftFrac = Math.max(0, (w.downUntil! - now) / CONFIG.catchWindowMs);
          const cx = sx(p.x), cy = sy(p.y) - 14 * K;
          const r = 5 * K;
          g.strokeStyle = 'rgba(16,19,31,.7)'; g.lineWidth = 2.5 * dpr;
          g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.stroke();
          g.strokeStyle = w.aura ? '#f0c45a' : '#7fc8e8'; g.lineWidth = 2 * dpr;
          g.beginPath(); g.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * leftFrac); g.stroke();
          // Manual catch: a seal in the ring marks a body that can still be clicked (client/catch.ts).
          if (!w.tried && !w.throwing) drawCatchMark(cx, cy, w.uid === catchHover);
        });
        if (w.throwing) {
          const thrown = fx.find((e) => e.k === 'throw' && e.uid === w.uid) as Extract<Fx, { k: 'throw' }> | undefined;
          const flying = thrown && performance.now() - thrown.t0 < 450;
          if (!flying) {
            const frame = Math.floor(t / 120) % 4;
            const id = has(`prop-seal-spin-${frame}`) ? `prop-seal-spin-${frame}` : 'icon-rune-seal';
            const img = sprite(id);
            const wob = Math.sin(t / 70) * 1.5;
            if (ready(img)) overlays.push(() => drawImg(img, p.x + wob / TILE, p.y - 0.05, 16, 16));
          }
        }
      } else {
        const bob = Math.abs(Math.sin(t / 220 + w.uid)) * -1.5;
        const target = f.lead && lp && Math.hypot(lp.x - p.x, lp.y - p.y) < 1.4;
        // A wild in a fight turns to face your creature.
        const face: Facing | undefined = target && lp ? (lp.x > p.x ? 'right' : 'left') : undefined;
        drawCreature(w.species, w.aura, p.x, p.y, lp ? lp.x > p.x : (w.uid % 2 === 0), { bob, key: `w${w.uid}`, face, t });
        const hgt = creatureHeight(w.species);
        overlays.push(() => {
          const top = sy(p.y) - Math.round(hgt * K) - 4 * dpr;
          tags.push({ s: `${name} Lv${w.level}`, x: sx(p.x), y: top - 6 * dpr, color: w.aura ? '#f0c45a' : w.visitor ? '#d9b3ff' : '#e8ecf4', pri: (w.aura ? 4 : 0) + (target ? 2 : 0) - (lp ? Math.hypot(lp.x - p.x, lp.y - p.y) / 100 : 0) });
          if (w.hp < w.maxHp || target) hpBar(sx(p.x), top - 2 * dpr, w.hp / w.maxHp);
        });
        if (w.aura) overlays.push(() => auraSparkle(p.x, p.y, t));
      }
    } });
  }

  if (lp && f.lead) {
    const target = f.wilds.find((w) => !w.downUntil && Math.hypot(w.x - lp.x, w.y - lp.y) < 1.5);
    const face = target ? wildPos(target, alpha).x > lp.x : lastLeadX < lp.x - 0.001 ? true : lastLeadX > lp.x + 0.001 ? false : leadFace;
    leadFace = face;
    lastLeadX = lp.x;
    const c = S.creatures.get(f.lead.id);
    if (c) items.push({ y: lp.y + 0.01, draw: () => {
      drawCreature(myArt(c.species, c.rolls.aura), c.rolls.aura, lp.x, lp.y, face, { bob: Math.abs(Math.sin(t / 180)) * -1, key: 'lead', face: target ? (face ? 'right' : 'left') : undefined, t });
      overlays.push(() => {
        // Your creature's name sits under its feet so it never collides with the wild it is fighting.
        const ls = `${displayName(c)} Lv${c.level}`, ly = sy(lp.y) + 13 * dpr + 3 * K;
        label(ls, sx(lp.x), ly, '#9fe0f2');
        const lw = g.measureText(ls).width;
        leadTag = { l: sx(lp.x) - lw / 2, r: sx(lp.x) + lw / 2, t: ly - 11 * dpr, b: ly + 2 * dpr };
        hpBar(sx(lp.x), sy(lp.y) + 3 * K, f.lead!.hp / f.lead!.maxHp, 30);
      });
      if (c.rolls.aura) overlays.push(() => auraSparkle(lp.x, lp.y, t));
    } });
  }
  // Other trainers on this hunt (client/wayfarers.ts), each fighting their own wild: theirs is faded and unlabelled.
  const wnow = performance.now();
  drawnWayfarers = stepWayfarers(dtMs, wnow);
  for (const w of drawnWayfarers) {
    if (!inView(w.x, w.y) && !inView(w.tx, w.ty)) continue;
    const r = w.rec, key = `way:${r.n}`;
    if (r.w) {
      const foeRight = w.x > w.wx;
      items.push({ y: w.wy, draw: () => drawCreature(r.w!.s, false, w.wx, w.wy, foeRight, { alpha: 0.42, key: `${key}:w`, face: foeRight ? 'right' : 'left', t }) });
    }
    if (r.lead) {
      const lead = r.lead, art = artOf(lead.species, lead.skin, lead.aura);
      const right = r.w ? w.wx > w.x : w.tx < w.x;
      // A lead in a fight swings now and then: enough to read as a fight, never as loud as yours.
      const a = anims.get(`${key}:l`);
      if (a && r.w && wnow - a.atk > 1400 + (a.seed % 700)) a.atk = wnow;
      items.push({ y: w.y + 0.01, draw: () => { drawCreature(art, !!lead.aura, w.x, w.y, right, { key: `${key}:l`, face: r.w ? (right ? 'right' : 'left') : undefined, t }); if (lead.aura) overlays.push(() => auraSparkle(w.x, w.y, t)); } });
    }
    items.push({ y: w.ty + 0.02, draw: () => {
      drawOtherTrainer(w, t, wnow);
      overlays.push(() => {
        const ny = sy(w.ty) + 13 * dpr + 3 * K;
        label(r.tag ? `[${r.tag}] ${r.n}` : r.n, sx(w.tx), ny, '#ead9ae', 10);
        if (w.show) showcase(w.show, w.tx, w.ty, wnow - w.show.t0, t);
        else if (w.emote) emoteOver(w.emote.id, w.tx, w.ty, wnow - w.emote.at, t);
        else if (w.bubble) speech(w.bubble.text, sx(w.tx), sy(w.ty) - 34 * K, Math.min(1, (w.bubble.until - wnow) / 400));
      });
    } });
  }
  const own = myShowcase(), em = myEmote();
  if (own) overlays.push(() => showcase(own, trainer.x, trainer.y, wnow - own.t0, t));
  else if (em) overlays.push(() => emoteOver(em.id, trainer.x, trainer.y, wnow - em.at, t));
  items.push({ y: trainer.y + 0.02, draw: () => drawTrainer(t) });
  items.sort((a, b) => a.y - b.y).forEach((i) => i.draw());
  overlays.forEach((o) => o());
  placeTags(tags, leadTag ? [leadTag] : []);

  // Effects
  const pnow = performance.now();
  for (let i = fx.length - 1; i >= 0; i--) {
    const e = fx[i];
    const age = pnow - e.t0;
    if (e.k === 'num') {
      const life = e.huge ? 1400 : 900;
      if (age > life) { fx.splice(i, 1); continue; }
      g.globalAlpha = age < life * 0.6 ? 1 : 1 - (age - life * 0.6) / (life * 0.4);
      const rise = reduce ? 0 : (age / 25) * dpr;
      if (e.huge) bigText(e.text, sx(e.x), sy(e.y) - 22 * K - rise * 0.6, e.color, 17);
      else label(e.text, sx(e.x), sy(e.y) - 22 * K - rise, e.color, e.big ? 15 : 12);
      g.globalAlpha = 1;
    } else if (e.k === 'move') {
      if (!drawMove(e, age, W, H)) fx.splice(i, 1);
    } else if (e.k === 'burst') {
      if (age > 700) { fx.splice(i, 1); continue; }
      for (let j = 0; j < e.n; j++) {
        const a = (j / e.n) * Math.PI * 2, d = (4 + age / 22) * K;
        g.fillStyle = e.color;
        g.globalAlpha = 1 - age / 700;
        g.fillRect(Math.round(sx(e.x) + Math.cos(a) * d) - K, Math.round(sy(e.y) - 8 * K + Math.sin(a) * d) - K, 2 * K, 2 * K);
      }
      g.globalAlpha = 1;
    } else if (e.k === 'banner') {
      if (age > 2600) { fx.splice(i, 1); continue; }
      g.globalAlpha = age > 2000 ? 1 - (age - 2000) / 600 : 1;
      bigText(e.text, W / 2, 70 * dpr, e.color, 24);
      g.globalAlpha = 1;
    } else if (e.k === 'throw') {
      // A seal arcs from the trainer to the body.
      if (age > 450) { if (age > 2000) fx.splice(i, 1); continue; }
      const w = f.wilds.find((x) => x.uid === e.uid);
      if (!w) { fx.splice(i, 1); continue; }
      const k = age / 450;
      const x = lerp(e.fx, w.x, k), y = lerp(e.fy, w.y, k) - Math.sin(k * Math.PI) * 1.2;
      const id = has(`prop-seal-spin-${Math.floor(age / 60) % 4}`) ? `prop-seal-spin-${Math.floor(age / 60) % 4}` : 'icon-rune-seal';
      const img = sprite(id);
      if (ready(img)) drawImg(img, x, y, 16, 16);
    }
  }

  if (S.trainer?.stopped) {
    // The card over the field (#stopped) says why; the scene just dims.
    g.fillStyle = 'rgba(10,12,20,.55)'; g.fillRect(0, 0, W, H);
  } else if (f.restingUntil > now) {
    g.fillStyle = 'rgba(10,12,20,.55)'; g.fillRect(0, 0, W, H);
    bigText(`Your team is resting: ${Math.ceil((f.restingUntil - now) / 1000)}s`, W / 2, H / 2, '#e8ecf4', 22);
  } else if (!f.lead && !(S.trainer && tutorialHolds(S.trainer))) {
    g.fillStyle = 'rgba(10,12,20,.55)'; g.fillRect(0, 0, W, H);
    bigText('Add a creature to your team to start hunting', W / 2, H / 2, '#e8ecf4', 18);
  }
  // The end of a walk between hunts fades out over the new hunt.
  drawTravel(g, W, H, dpr, t, cardBottom(t), ...walkers);
}
let leadFace = true;

// ---------------- move effects (watch mode) ----------------
// Code-drawn pixel effects, one look per move kind and one motion per element: flame rises, tide splashes and
// falls, grove leaves sway down, storm crackles fast, stone chunks drop hard, frost glints and hangs, shade pulls
// inward, light flares outward. Every particle is worked out from the effect's seed and age, so an effect is one
// small object however many pixels it draws. Signatures run longest and add a pillar, a double ring and the
// element's glyph.
const MOVE_MS: Record<AnimKind, number> = { jab: 320, slash: 380, projectile: 520, beam: 520, burst: 600, wave: 900, signature: 1250 };
/** Where the effect starts landing: a projectile's flight time, a Signature's pillar drop. */
const LAND_MS: Partial<Record<AnimKind, number>> = { projectile: 200, beam: 200, signature: 220 };
/** An Awakened move runs this much longer than a Signature, so its gilt rune circle has time to read. */
const AWK_STRETCH = 1.4;
/** The awakened gilt (art/rig3d/parts/awakened.ts GILT): shadow, mid, light, highlight. */
const GILT = ['#6a3810', '#e5a632', '#ffd468', '#fff3c0'];
/** Five runes, 3 x 5 source pixels each, for an Awakened move's circle (the sprites' rune halos in pixels). */
const RUNE_BITS = [
  ['.#.', '###', '.#.', '.#.', '.#.'],
  ['#.#', '###', '.#.', '.#.', '.#.'],
  ['#..', '##.', '#.#', '#..', '#..'],
  ['..#', '.#.', '#..', '.#.', '..#'],
  ['##.', '#.#', '#..', '#..', '#..'],
];

/** An element's mid, highlight and shadow from its creature-palette ramp (client/hitfx.ts). */
function tintOf(el: Element): [string, string, string] {
  const r = EL_RAMP[el];
  return [r[2], r[4], r[1]];
}

/** The impact star (client/hitfx.ts) over the first part of an effect, t0 = share of the effect it lasts. */
function spark(el: Element, cx: number, cy: number, t: number, t0: number) {
  if (t >= t0) return;
  const k = t / t0;
  g.globalAlpha = k < 0.67 ? 1 : (1 - k) * 3;
  stamp(g, sparkSprite(el, Math.floor(k * 3)), cx, cy, K);
}

/** A repeatable 0-1 value per effect, particle and channel. */
function rnd(seed: number, i: number, k: number) {
  const v = Math.sin(seed * 12.9898 + i * 78.233 + k * 37.719) * 43758.5453;
  return v - Math.floor(v);
}

function px(x: number, y: number, size: number, color: string) {
  const s = Math.max(1, Math.round(size * K));
  g.fillStyle = color;
  g.fillRect(Math.round(x - s / 2), Math.round(y - s / 2), s, s);
}

/** n particles bursting from (cx, cy) in screen pixels, moving as the element does. t runs 0 to 1. */
function particles(el: Element, seed: number, cx: number, cy: number, t: number, n: number, reach: number, size: number) {
  const [base, pale] = tintOf(el);
  const sprites: [number, number, boolean, boolean][] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rnd(seed, i, 1) * 0.9;
    const sp = reach * (0.55 + rnd(seed, i, 2) * 0.6) * K;
    let dx = Math.cos(a) * sp * t, dy = Math.sin(a) * sp * t * 0.7;
    let sz = size * (rnd(seed, i, 3) > 0.55 ? 1.5 : 1);
    switch (el) {
      case 'flame': dy -= t * 14 * K; dx *= 0.6; sz *= 1 - t * 0.5; break;
      case 'tide': dy += (t * t * 26 - t * 10) * K; break;
      case 'grove': dx += Math.sin(t * 7 + i) * 3 * K; dy += t * 8 * K; break;
      case 'storm': dx += (rnd(seed, i + Math.floor(t * 8), 4) - 0.5) * 6 * K; dy += (rnd(seed, i + Math.floor(t * 8), 5) - 0.5) * 6 * K; break;
      case 'stone': dy += (t * t * 34 - t * 8) * K; sz *= 1.4; break;
      case 'frost': dx *= 0.7; dy = dy * 0.7 + t * 4 * K; if ((Math.floor(t * 10) + i) % 3 === 0) sz *= 1.6; break;
      case 'shade': dx = Math.cos(a) * sp * (1 - t); dy = Math.sin(a) * sp * (1 - t) * 0.7; break;
      case 'light': dx *= 1.3; dy *= 1.3; break;
    }
    // A few stay plain pixels for sparkle; the rest are tiny outlined sprites in the element's ramp.
    if (i % 5 === 4) px(cx + dx, cy + dy, Math.min(sz, 1.4), i % 2 ? pale : base);
    else sprites.push([cx + dx, cy + dy, sz >= 1.75 && !(el === 'shade' && t > 0.6), i % 2 === 1]);
  }
  // Every outline first, then every fill, so overlapping particles read as one outlined cluster.
  for (const [x, y, big, flip] of sprites) stamp(g, particleSprite(el, big, 'line'), x, y, K, flip);
  for (const [x, y, big, flip] of sprites) stamp(g, particleSprite(el, big, 'fill'), x, y, K, flip);
}

/** A ring of pixels, radius r in source pixels, flattened to the ground. */
function pixelRing(el: Element, cx: number, cy: number, r: number, n: number, size: number, color?: string) {
  const [base, pale] = tintOf(el);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    px(cx + Math.cos(a) * r * K, cy + Math.sin(a) * r * K * 0.55, size, color ?? (i % 2 ? base : pale));
  }
}

function drawGlyph(el: Element, cx: number, cy: number, scale: number, ink = '#10131f') {
  const rows = GLYPH[el];
  const s = Math.max(1, Math.round(scale * K));
  const [base, pale] = tintOf(el);
  for (let y = 0; y < 7; y++) for (let x = 0; x < 7; x++) {
    if (rows[y][x] !== '#') continue;
    g.fillStyle = ink;
    g.fillRect(Math.round(cx + (x - 3.5) * s) - 1, Math.round(cy + (y - 3.5) * s) - 1, s + 2, s + 2);
  }
  for (let y = 0; y < 7; y++) for (let x = 0; x < 7; x++) {
    if (rows[y][x] !== '#') continue;
    g.fillStyle = y < 3 ? pale : base;
    g.fillRect(Math.round(cx + (x - 3.5) * s), Math.round(cy + (y - 3.5) * s), s, s);
  }
}

/**
 * An Awakened move's circle: eight gilt runes standing on the ground round the impact, thrown out to their radius
 * at once, then turning slowly as the effect fades. Each rune is ink-outlined like the sprites.
 */
function runeCircle(cx: number, cy: number, t: number, seed: number) {
  const n = 8, rr = (10 + Math.min(1, t * 4) * 24) * K, spin = t * 1.1 + seed;
  const s = Math.max(1, Math.round(K));
  for (const under of [true, false]) for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + spin;
    const x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr * 0.55;
    const bits = RUNE_BITS[i % RUNE_BITS.length];
    for (let by = 0; by < 5; by++) for (let bx = 0; bx < 3; bx++) {
      if (bits[by][bx] !== '#') continue;
      const X = Math.round(x + (bx - 1.5) * s), Y = Math.round(y + (by - 5) * s);
      g.fillStyle = under ? '#2a1606' : by < 2 ? GILT[3] : GILT[2];
      if (under) g.fillRect(X - 1, Y - 1, s + 2, s + 2); else g.fillRect(X, Y, s, s);
    }
  }
}

/** Draws one move effect; false once it has finished. */
function drawMove(e: Extract<Fx, { k: 'move' }>, age: number, W: number, H: number): boolean {
  const dur = MOVE_MS[e.kind] * (e.awk ? AWK_STRETCH : 1);
  if (age > dur) return false;
  if (age < 0) return true;
  const [base, pale, deep] = tintOf(e.el);
  const cx = sx(e.x), cy = sy(e.y) - 10 * K;
  const land = LAND_MS[e.kind] ?? 0;
  const t = Math.max(0, (age - land) / (dur - land));
  const fade = 1 - t;
  switch (e.kind) {
    case 'jab':
      spark(e.el, cx, cy, t, 0.3);
      g.globalAlpha = fade;
      particles(e.el, e.seed, cx, cy, t, 6, 11, 1.2);
      break;
    case 'slash': {
      // Three pixel streaks wipe across the target, then the element's particles.
      const wipe = Math.min(1, age / 160);
      g.globalAlpha = age < 160 ? 1 : Math.max(0, 1 - (age - 160) / 200);
      // Dark under-strokes first, so the streaks carry the creatures' outline.
      for (const under of [true, false]) for (let s = -1; s <= 1; s++) {
        const steps = 9;
        for (let j = 0; j < steps * wipe; j++) {
          const k = j / (steps - 1);
          const sz = s === 0 ? 2 : 1.4;
          px(cx + (-10 + 20 * k + s * 4) * K, cy + (-9 + 18 * k - s * 2) * K, under ? sz + 1 : sz, under ? '#08070c' : j % 2 ? pale : base);
        }
      }
      g.globalAlpha = fade;
      particles(e.el, e.seed, cx, cy, t, 8, 14, 1.3);
      break;
    }
    case 'projectile':
    case 'beam': {
      if (age < land) {
        // An orb with a short trail flies from the lead to the target.
        const ox = sx(e.fx), oy = sy(e.fy) - 10 * K;
        for (let j = 3; j >= 0; j--) {
          const k = Math.max(0, age / land - j * 0.09);
          g.globalAlpha = 1 - j * 0.22;
          stamp(g, particleSprite(e.el, j === 0), lerp(ox, cx, k), lerp(oy, cy, k) - Math.sin(k * Math.PI) * 6 * K, K);
        }
      } else {
        spark(e.el, cx, cy, t, 0.35);
        g.globalAlpha = fade;
        particles(e.el, e.seed, cx, cy, t, 10, 16, 1.5);
      }
      break;
    }
    case 'wave': {
      // A Wave: the ground ripples out from the target to the edge of its reach (WAVE_RADIUS tiles), three rings
      // apart, with the element's particles riding the front. Every wild the front passes takes the splash.
      const reach = WAVE_RADIUS * TILE;
      const front = 4 + Math.min(1, t * 1.25) * reach;
      // The ground under the front glows in the element's color, so the reach reads even in a still frame.
      g.globalAlpha = 0.22 * fade;
      g.fillStyle = base;
      g.beginPath(); g.ellipse(cx, cy + 6 * K, front * K, front * K * 0.55, 0, 0, Math.PI * 2); g.fill();
      spark(e.el, cx, cy + 4 * K, t, 0.25);
      for (let r = 0; r < 3; r++) {
        const k = t - r * 0.14;
        if (k <= 0) continue;
        const rad = 4 + Math.min(1, k * 1.25) * reach;
        g.globalAlpha = Math.max(0, (1 - k) * (r ? 0.75 : 1));
        pixelRing(e.el, cx, cy + 6 * K, rad, 22 + Math.round(rad * 1.1), r ? 1.8 : 2.8, r === 2 ? deep : undefined);
      }
      g.globalAlpha = fade;
      particles(e.el, e.seed, cx, cy + 4 * K, t, 22, reach * 0.9, 1.8);
      break;
    }
    case 'burst': {
      g.globalAlpha = fade;
      pixelRing(e.el, cx, cy + 6 * K, 6 + t * 22, 22, 1.8);
      spark(e.el, cx, cy, t, 0.35);
      g.globalAlpha = fade;
      particles(e.el, e.seed, cx, cy, t, 14, 22, 1.7);
      break;
    }
    case 'signature': {
      // A pillar of the element drops on the target; on impact a flash, rays and two rings spread over the
      // ground, the element's glyph rises, and the edges of the field glow in its color for a moment. Nothing
      // covers the scene and nothing waits on it. An Awakened move is the same, grander and in gilt: a gold sheath on
      // the pillar, a gilt-edged glyph, a gold edge glow and a circle of gilt runes round the impact.
      const awk = !!e.awk;
      if (age < land) {
        const k = age / land;
        g.globalAlpha = 0.85;
        if (awk) { g.fillStyle = GILT[2]; g.fillRect(Math.round(cx - 10 * K), 0, Math.round(20 * K), Math.round(cy * k)); }
        g.fillStyle = pale; g.fillRect(Math.round(cx - 7 * K), 0, Math.round(14 * K), Math.round(cy * k));
        g.fillStyle = base; g.fillRect(Math.round(cx - 3 * K), 0, Math.round(6 * K), Math.round(cy * k));
        break;
      }
      const edge = Math.max(0, 1 - t * 2.2);
      if (edge > 0) {
        g.globalAlpha = edge * (awk ? 0.5 : 0.4);
        const band = Math.round((awk ? 16 : 12) * dpr);
        g.fillStyle = awk ? GILT[1] : base;
        g.fillRect(0, 0, W, band); g.fillRect(0, H - band, W, band); g.fillRect(0, 0, band, H); g.fillRect(W - band, 0, band, H);
      }
      if (t < 0.3) {
        const k = 1 - t / 0.3;
        g.globalAlpha = k * 0.85;
        if (awk) { g.fillStyle = GILT[3]; g.fillRect(Math.round(cx - 10 * K * k), 0, Math.round(20 * K * k), Math.round(cy + 6 * K)); }
        g.fillStyle = pale; g.fillRect(Math.round(cx - 7 * K * k), 0, Math.round(14 * K * k), Math.round(cy + 6 * K));
        g.globalAlpha = k * 0.6;
        g.beginPath(); g.ellipse(cx, cy + 4 * K, (12 + t * 40) * K, (7 + t * 22) * K, 0, 0, Math.PI * 2); g.fill();
      }
      // Rays: twelve dotted spokes thrown out from the impact.
      g.globalAlpha = fade;
      for (let r = 0; r < 12; r++) {
        const a = (r / 12) * Math.PI * 2 + 0.13;
        for (let d = 6 + t * 18; d < 10 + t * 44; d += 4) px(cx + Math.cos(a) * d * K, cy + Math.sin(a) * d * K * 0.6, 1.6, r % 2 ? base : pale);
      }
      pixelRing(e.el, cx, cy + 6 * K, 10 + t * 42, 52, 2.6);
      pixelRing(e.el, cx, cy + 6 * K, 5 + t * 24, 34, 2, deep);
      if (awk) {
        pixelRing(e.el, cx, cy + 6 * K, 14 + t * 52, 64, 2.2, GILT[2]);
        g.globalAlpha = Math.min(1, fade * 2);
        runeCircle(cx, cy + 6 * K, t, e.seed);
        g.globalAlpha = fade;
      }
      particles(e.el, e.seed, cx, cy, t, awk ? 44 : 36, awk ? 44 : 36, 2.4);
      g.globalAlpha = Math.min(1, fade * 1.5);
      drawGlyph(e.el, cx, cy - (24 + t * 12) * K, awk ? 2.8 : 2.4, awk ? GILT[1] : undefined);
      break;
    }
  }
  g.globalAlpha = 1;
  return true;
}

// ---------------- wayfarers (client/wayfarers.ts) ----------------
let drawnWayfarers: Wayfarer[] = [];
const wayAnims = new Map<string, Anim>();

function drawOtherTrainer(w: Wayfarer, t: number, now: number) {
  const fit = trainerSprite(w.rec.o);
  if (!sheetInfo(fit)) return;
  let a = wayAnims.get(w.rec.n);
  if (!a) { a = { face: 'front', x: 0, y: 0, moved: 0, atk: -1e9, hit: -1e9, seed: Math.random() * 5000, seen: now, head: heading() }; wayAnims.set(w.rec.n, a); }
  if (wayAnims.size > 64) for (const k of wayAnims.keys()) if (!drawnWayfarers.some((d) => d.rec.n === k)) wayAnims.delete(k);
  a.face = FACING[w.dir];
  a.moved = w.moved;
  drawSheet(fit, false, w.tx, w.ty + 0.03, a, t, {});
}

/** An Aura just caught, floating up over its catcher in a gold glow, with its name. */
function showcase(sh: { species: string; skin?: string; t0: number }, x: number, y: number, age: number, t: number) {
  const k = age / SHOW_MS;
  const fade = k < 0.08 ? k / 0.08 : k > 0.85 ? (1 - k) / 0.15 : 1;
  const rise = Math.min(1, age / 900);
  const hy = y - 1.9 - rise * 0.5 + Math.sin(t / 380) * 0.08;
  const cx = sx(x), cy = sy(hy) - 10 * K;
  const glow = g.createRadialGradient(cx, cy, 2 * K, cx, cy, 26 * K);
  glow.addColorStop(0, 'rgba(255,226,140,0.75)'); glow.addColorStop(1, 'rgba(255,200,90,0)');
  g.globalAlpha = fade; g.fillStyle = glow;
  g.beginPath(); g.arc(cx, cy, 26 * K, 0, Math.PI * 2); g.fill();
  g.globalAlpha = 1;
  drawCreature(artOf(sh.species, sh.skin, true), true, x, hy, true, { alpha: fade, bob: 0 });
  if (fade > 0.3) auraSparkle(x, hy, t);
  g.globalAlpha = fade;
  label(`Aura ${SPECIES[sh.species]?.name ?? ''}`, cx, sy(hy) - 30 * K, '#f0c45a', 12);
  g.globalAlpha = 1;
}

/** An emote over a trainer's head (as in Kinhall): it pops in, bobs, and fades. */
const emoteImgs = new Map<string, HTMLImageElement>();
function emoteOver(id: string, x: number, y: number, age: number, t: number) {
  let img = emoteImgs.get(id);
  if (!img) { img = new Image(); img.src = emoteIcon(id); emoteImgs.set(id, img); }
  if (!ready(img)) return;
  const pop = Math.min(1, age / 140), fade = Math.max(0, Math.min(1, (EMOTE_MS - age) / 300));
  const size = 11 * K * 1.3 * (0.6 + 0.4 * pop), bob = Math.sin(t / 160) * K;
  const cx = sx(x), cy = sy(y) - 40 * K + bob;
  g.globalAlpha = fade;
  g.fillStyle = 'rgba(244,232,204,.95)'; g.strokeStyle = '#7a5a2e'; g.lineWidth = Math.max(1, dpr);
  g.beginPath(); g.roundRect(cx - size / 2 - 3 * K, cy - size / 2 - 3 * K, size + 6 * K, size + 6 * K, 3 * K); g.fill(); g.stroke();
  g.drawImage(img, Math.round(cx - size / 2), Math.round(cy - size / 2), Math.round(size), Math.round(size));
  g.globalAlpha = 1;
}

/** A chat line over a trainer's head. */
function speech(text: string, x: number, y: number, alpha: number) {
  g.font = `500 ${Math.round(12 * dpr)}px Inter, sans-serif`;
  const words = text.split(' '), lines: string[] = [];
  let cur = '';
  for (const wd of words) { const nx = cur ? `${cur} ${wd}` : wd; if (g.measureText(nx).width > 150 * dpr && cur) { lines.push(cur); cur = wd; } else cur = nx; }
  if (cur) lines.push(cur);
  const lh = 14 * dpr, pad = 6 * dpr;
  const bw = Math.max(...lines.map((l) => g.measureText(l).width)) + pad * 2, bh = lines.length * lh + pad * 1.2;
  const bx = Math.round(x - bw / 2), by = Math.round(y - bh);
  g.globalAlpha = Math.max(0, alpha);
  g.fillStyle = 'rgba(244,234,210,.96)'; g.strokeStyle = '#8a6b33'; g.lineWidth = 1.5 * dpr;
  g.beginPath(); g.roundRect(bx, by, bw, bh, 6 * dpr); g.fill(); g.stroke();
  g.beginPath(); g.moveTo(x - 4 * dpr, by + bh - 0.5); g.lineTo(x, by + bh + 6 * dpr); g.lineTo(x + 4 * dpr, by + bh - 0.5); g.closePath(); g.fill();
  g.fillStyle = '#2a1d10'; g.textAlign = 'center';
  lines.forEach((l, i) => g.fillText(l, x, by + pad + lh * (i + 0.75)));
  g.globalAlpha = 1;
}

/** The other trainer under a page point (client px), or null: a click opens their trainer card. */
export function wayfarerAt(clientX: number, clientY: number): string | null {
  if (!canvas || saver || !onScreen() || travelCovers()) return null;
  const r = canvas.getBoundingClientRect();
  const px = (clientX - r.left) * dpr, py = (clientY - r.top) * dpr;
  for (const w of drawnWayfarers) {
    const dx = px - sx(w.tx), dy = py - (sy(w.ty) - 14 * K);
    if (Math.abs(dx) < 10 * K && Math.abs(dy) < 18 * K) return w.rec.n;
  }
  return null;
}

/** Signposts drawn this frame, in canvas pixels: the area each path leads to. */
let drawnSigns: { to: string; x: number; top: number; bottom: number }[] = [];
/** The area a signpost under a page point (client px) leads to, or null: a click opens it on the map. */
export function signpostAt(clientX: number, clientY: number): string | null {
  if (!canvas || saver || !onScreen() || travelCovers()) return null;
  const r = canvas.getBoundingClientRect();
  const px = (clientX - r.left) * dpr, py = (clientY - r.top) * dpr;
  for (const s of drawnSigns) if (Math.abs(px - s.x) < Math.max(14 * K, 22 * dpr) && py > s.top - 14 * dpr && py < s.bottom + 4 * dpr) return s.to;
  return null;
}

/** Whether the hunt scene is drawn now (the server sends other trainers only then). */
export const fieldWatching = () => !saver && shownNow();

function auraSparkle(x: number, y: number, t: number) {
  for (let j = 0; j < 4; j++) {
    const a = t / 500 + j * 1.57;
    const px = sx(x) + Math.cos(a) * 11 * K, py = sy(y) - 12 * K + Math.sin(a) * 7 * K;
    g.fillStyle = j % 2 ? '#f0c45a' : '#f1dcff';
    g.fillRect(Math.round(px) - K / 2, Math.round(py) - 1.5 * K, K, 3 * K);
    g.fillRect(Math.round(px) - 1.5 * K, Math.round(py) - K / 2, 3 * K, K);
  }
}

function posOf(uid: number) {
  const w = S.field?.wilds.find((x) => x.uid === uid) ?? S.prevField?.wilds.find((x) => x.uid === uid);
  return w ? { x: w.x, y: w.y } : null;
}

// Read by the browser checks: queued effects must stay at zero in a hidden tab.
(globalThis as { miFx?: () => number }).miFx = () => fx.length;
/** Move effects on screen now, by kind (browser checks and screenshots wait on them). */
(globalThis as { miFxMoves?: () => string[] }).miFxMoves = () => fx.flatMap((e) => (e.k === 'move' ? [e.kind] : []));
document.addEventListener('visibilitychange', () => { if (document.hidden) fx.length = 0; });

export function fieldEvents(evs: GameEvent[]) {
  // Effects only mean something on screen. A hidden tab gets no animation
  // frames to age them out, so queuing them would pile up thousands an hour;
  // on another page the field isn't drawn at all.
  if (saver || document.hidden || !shownNow()) return;
  const t0 = performance.now();
  for (const e of evs) {
    if (e.k === 'hit') {
      // The attacker swings now; the target flashes when the blow lands, two frames in.
      const atk = anims.get(e.by === 'lead' ? 'lead' : `w${e.uid}`), tgt = anims.get(e.by === 'lead' ? `w${e.uid}` : 'lead');
      if (atk) atk.atk = t0;
      if (tgt) tgt.hit = t0 + 2 * ATK_MS;
      if (e.by === 'lead') {
        const p = posOf(e.uid);
        const mv = MOVE[e.mv];
        const sig = mv?.slot === 'signature' || mv?.slot === 'awakened';
        // A Wave's splash on a wild next to the target: its number and a small hit; the Wave's ring, drawn on
        // the target, already shows the move.
        if (e.splash) {
          if (p) fx.push({ k: 'num', x: p.x, y: p.y, text: `${dmgText(e.dmg)}${e.crit ? '!' : ''}`, color: e.mult > 1 ? '#f0c45a' : e.mult < 1 ? '#9aa6c0' : '#ffffff', t0 });
          if (p && mv && !reduce) fx.push({ k: 'move', kind: 'jab', el: mv.element, x: p.x, y: p.y, fx: p.x, fy: p.y, t0: t0 + 120, seed: (t0 % 1000) + e.uid });
          continue;
        }
        if (p) fx.push({ k: 'num', x: p.x, y: p.y, text: `${dmgText(e.dmg)}${e.crit ? '!' : ''}`, color: e.mult > 1 ? '#f0c45a' : e.mult < 1 ? '#9aa6c0' : '#ffffff', t0, big: e.crit || mv?.slot === 'heavy', huge: sig });
        // A move other than the basic attack shows its name in its element's color, with the match-up in words
        // (watch mode, battle design 2.4). A Signature's name is larger and stays longer.
        if (p && mv && mv.slot !== 'basic') {
          const word = effectWord(e.mult);
          fx.push({ k: 'num', x: p.x, y: p.y - (sig ? 1.1 : 0.45), text: word ? `${mv.name} · ${word}` : mv.name, color: sig ? '#ffe2a0' : ELEMENTS[mv.element].color, t0, huge: sig });
        }
        // The hit itself: one small effect per blow, none with reduced motion.
        const lead = S.field?.lead;
        if (p && mv && !reduce) fx.push({ k: 'move', kind: mv.anim.kind, el: mv.element, x: p.x, y: p.y, fx: lead?.x ?? p.x, fy: lead?.y ?? p.y, t0, seed: (t0 % 1000) + e.uid, awk: mv.slot === 'awakened' });
      } else if (S.field?.lead) {
        fx.push({ k: 'num', x: S.field.lead.x, y: S.field.lead.y, text: dmgText(e.dmg), color: '#ff8a80', t0 });
      }
    } else if (e.k === 'down') {
      const p = posOf(e.uid);
      if (p) fx.push({ k: 'num', x: p.x, y: p.y - 0.5, text: `+${e.coins} coins`, color: '#f0c45a', t0 });
    } else if (e.k === 'throw') {
      fx.push({ k: 'throw', uid: e.uid, fx: trainer.x, fy: trainer.y - 0.6, t0 });
      const p = posOf(e.uid);
      if (p && !trainer.moving) trainer.dir = dirTo(p.x - trainer.x, p.y - trainer.y);
      trainer.throwAt = t0;
    } else if (e.k === 'catch') {
      const p = posOf(e.uid);
      if (p && !reduce) fx.push({ k: 'burst', x: p.x, y: p.y, color: e.creature.rolls.aura ? '#f0c45a' : '#7fc8e8', t0, n: e.creature.rolls.aura ? 16 : 10 });
      if (e.creature.rolls.aura) fx.push({ k: 'banner', text: 'Aura caught!', color: '#f0c45a', t0 });
    } else if (e.k === 'miss') {
      const p = posOf(e.uid);
      if (p) fx.push({ k: 'num', x: p.x, y: p.y, text: 'broke free', color: '#e8ecf5', t0 });
    } else if (e.k === 'levelup') {
      // Level-ups stay quiet on the field; only a level that opens a region or system gets a banner, from the same
      // switch-aware list as the hunt log (ranked moves the Arena and Battle Plan, the Trail takes the bosses). When a
      // level opens several, the banner names the biggest news.
      const u = opensBetween(e.level - 1, e.level).sort((a, b) => headline(a.id) - headline(b.id))[0];
      if (u) fx.push({ k: 'banner', text: `${u.name} unlocked`, color: '#7fc8e8', t0 });
    } else if (e.k === 'spawn' && e.wild.aura) {
      fx.push({ k: 'banner', text: 'An aura creature appeared!', color: '#f0c45a', t0 });
    }
  }
}

/** New hunt: re-centre the camera and drop the trainer's trail. */
export function resetBackground() {
  cam.ok = false;
  camTrail.length = 0;
  trail.length = 0;
  trainer = { x: 0, y: 0, dir: 'down', walk: 0, moving: false, throwAt: -1e9, head: heading() };
}

